<template>
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <a href="#">{{ translations('navbar.budget_control') }}</a>
        </li>
        <li class="breadcrumb-item active" aria-current="page">{{ translations('budget.archive_budget') }}</li>
      </ol>
    </nav>
    <div class="container-fluid main-container">
      <div class="form-inline mb-5">
        <div class="form-group mr-2">
          <a class="btn btn-primary" href="new-budget">{{ translations('common.addition') }}</a>
        </div>
        <div class="form-group">
          <div class="dropdown">
            <button id="filterByYear" class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              {{ translations('budget.fiscal_year') }}
            </button>
            <div class="dropdown-menu">
              <a class='dropdown-item' v-on:click='clearFilterByYear(); selectedYear=0; triggerFilter();'>{{ translations('budget.fiscal_year') }}</a>
              <a class="dropdown-item" v-for="(year, i) in budgetYears" :selected="selectedYear === year" v-on:click="selectedYear = year; triggerFilter(); selectYear(year)" :key="`budget_year-${i}`">{{year}}</a>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex align-items-center mb-3">
        <h4 class="font-weight-bold mb-0">{{ translations('budget.entire_company') }}</h4>
      </div>
      <div class="mb-4">
        <div class="form-inline justify-content-end"><!-- VAT -->
          <div class="form-check">
            <input class="form-check-input" type="checkbox" value="" id="defaultCheckTax" ref="defaultCheckTax" v-model="taxIncluded" @change="toggleTax()">
            <label class="form-check-label" for="defaultCheckTax">{{ translations('common.tax_included') }}</label>
          </div>
        </div>
        <div class="table-scroll">
          <v-data-table
            class="table table-striped table-800"
            item-key="name"
            :disable-sort="true"
            :headers="[
              { text: translations('budget.month'), value: 'month' },
              { text: translations('budget.sales_budget'), value: 'sales_budget' },
              { text: translations('budget.sales_performance'), value: '' },
              { text: translations('budget.sales_achievement_rate'), value: 'sales_achievement' },
              { text: translations('budget.sales_compared_previous_period'), value: 'sales_yoy' },
              { text: translations('budget.cost_price'), value: 'row_cost' },
              { text: translations('budget.shipping_cost'), value: '' },
              { text: translations('budget.shipping_cost_ratio'), value: '' },
              { text: translations('budget.gross_profit_budget'), value: 'gross_profit_budget' },
              { text: translations('budget.gross_profit_performance'), value: 'gross_profit_result' },
              { text: translations('budget.gross_profit_performance'), value: 'gross_profit_achievement' },
              { text: translations('budget.gross_profit_comparison_previous_period'), value: 'gross_profit_yoy' },
            ]"
            :items="wholeBudgets"
            hide-default-footer
            :page.sync="page"
            :items-per-page="itemsPerPage"
            @page-count="pageCount = $event"
          >
            <template v-slot:body="{ items }">
              <tbody style="white-space: nowrap;">
                <template v-for="(budget, i) in items">
                  <tr :key="`budget-${i}`">
                    <td :class="(i == 0) ? 'font-weight-bold' : ''">{{getMonthName(budget)}}</td>
                    <td>{{ toVatFormat(budget.sales_budget) }}</td>
                    <td>{{ toVatFormat(budget.sales_result) }}</td>
                    <td>{{ budget.sales_achievement }}</td>
                    <td>{{ (budget.sales_yoy && budget.sales_yoy !== "-") ? Number(budget.sales_yoy.replace("%", "").replace(",", "")).toLocaleString() + "%" : budget.sales_yoy }}</td>
                    <td>{{ toVatFormat(budget.row_cost) }}</td>
                    <td>{{ toVatFormat(budget.domestic_cost) }}</td>
                    <td>{{ parseInt(budget.sales_budget) != 0 ? Number(Math.round((parseInt(budget.domestic_cost) / parseInt(budget.sales_budget)) * 100)).toLocaleString() + "%" : "-" }}</td>
                    <td>{{ toVatFormat(budget.gross_profit_budget) }}</td>
                    <td>{{ toVatFormat(budget.gross_profit_result) }}</td>
                    <td>
                      <div class="d-flex align-items-center min-width-150">
                        <span class="table-percent-label">{{ budget.gross_profit_achievement ? Number(budget.gross_profit_achievement.replace("%", "").replace(",", "")).toLocaleString() + "%" : '' }}</span>
                        <div class="progress w-100"><div class="progress-bar progress-bar-striped bg-warning" role="progressbar" :style="'width:' + budget.gross_profit_achievement + ''" :aria-valuenow="budget.gross_profit_achievement" aria-valuemin="0" aria-valuemax="100"></div></div>
                      </div>
                    </td>
                    <td>{{ (budget.gross_profit_yoy && budget.gross_profit_yoy !== "-") ? Number(budget.gross_profit_yoy.replace("%", "").replace(",", "")).toLocaleString() + "%" : budget.gross_profit_yoy }}</td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-data-table>
          <v-pagination v-model="page" :length="pageCount" :total-visible="10"></v-pagination>
        </div>
      </div>
      <h4 class="font-weight-bold mb-4 mt-5">{{ translations('budget.by_representative') }}</h4>
      <template v-for="(contact, i) in contactBudgets">
        <div :key="`contact-${i}`" class="mb-4">
          <div class="d-flex align-items-center mb-3">
            <button class="btn btn-info mr-2" type="button" data-toggle="collapse" :href="'#collapseContacts' + contact.budget_id" aria-expanded="false" aria-controls="collapseContacts">{{contact.contacts_name}}<i class="fas fa-angle-down ml-2"></i></button>
            <a href="#" class="btn btn-sm btn-link decoration-underline" @click="openSingleBudget(contact)">{{ translations('budget.edit') }}</a>
          </div>
          <div class="table-scroll collapse" :id="'collapseContacts' + contact.budget_id">
            <v-data-table
              class="table table-striped table-800"
              item-key="name"
              :disable-sort="true"
              :headers="[
                { text: translations('budget.month') },
                { text: translations('budget.sales_budget') },
                { text: translations('budget.sales_performance') },
                { text: translations('budget.sales_achievement_rate') },
                { text: translations('budget.sales_compared_previous_period') },
                { text: translations('budget.cost_price') },
                { text: translations('budget.gross_profit_budget') },
                { text: translations('budget.gross_profit_performance') },
                { text: translations('budget.gross_profit_achievement_rate') },
                { text: translations('budget.gross_profit_comparison_previous_period') },
              ]"
              :items="contact.months"
              hide-default-footer
              :page.sync="contactPages[i]"
              :items-per-page="itemsPerPage"
              @page-count="contactPageCounts[i] = $event"
            >
              <template v-slot:body="{ items }">
                <tbody style="white-space: nowrap;">
                  <template v-for="(budget, i) in items">
                    <tr :key="`month-${i}`">
                      <td :class="(i == 0) ? 'font-weight-bold' : ''">{{getMonthName(budget)}}</td>
                      <td>{{ toVatFormat(budget.sales_budget) }}</td>
                      <td>{{ toVatFormat(budget.sales_result) }}</td>
                      <td>{{ budget.sales_achievement }}</td>
                      <td>{{ budget.sales_yoy }}</td>
                      <td>{{ toVatFormat(budget.row_cost) }}</td>
                      <td>{{ toVatFormat(budget.gross_profit_budget) }}</td>
                      <td>{{ toVatFormat(budget.gross_profit_result) }}</td>
                      <td>
                        <div class="d-flex align-items-center min-width-150">
                          <span class="table-percent-label">{{ budget.gross_profit_achievement }}</span>
                          <div class="progress w-100"><div class="progress-bar progress-bar-striped bg-info" role="progressbar" :style="'width:' + budget.gross_profit_achievement + ''" :aria-valuenow="budget.gross_profit_achievement" aria-valuemin="0" aria-valuemax="100"></div></div>
                        </div>
                      </td>
                      <td>{{ budget.gross_profit_yoy }}</td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-data-table>
            <v-pagination v-model="contactPages[i]" :length="contactPageCounts[i]" :total-visible="10"></v-pagination>
          </div>
        </div>
        <hr :class="i != contactBudgets.length - 1 ? '' : 'hidden'" :key="`contact-hr-${i}`">
      </template>
      <h4 class="font-weight-bold mb-4 mt-5">{{ translations('budget.by_category') }}</h4>
      <template v-for="(category, i) in categoryBudgets">
        <div :key="`category-${i}`" class="mb-4">
          <div class="d-flex align-items-center mb-3">
            <button class="btn btn-info" type="button" data-toggle="collapse" :href="'#collapseCategory' + category.category_id" aria-expanded="false" aria-controls="collapseCategory">{{category.category_name}}<i class="fas fa-angle-down ml-2"></i></button>
          </div>
          <div class="table-scroll collapse" :id="'collapseCategory' + category.category_id">
            <v-data-table
              class="table table-striped table-800"
              item-key="name"
              :disable-sort="true"
              :headers="[
                { text: translations('budget.month') },
                { text: translations('budget.sales_budget') },
                { text: translations('budget.sales_performance') },
                { text: translations('budget.sales_achievement_rate') },
                { text: translations('budget.sales_compared_previous_period') },
                { text: translations('budget.cost_price') },
                { text: translations('budget.gross_profit_budget') },
                { text: translations('budget.gross_profit_performance') },
                { text: translations('budget.gross_profit_achievement_rate') },
                { text: translations('budget.gross_profit_comparison_previous_period') },
              ]"
              :items="category.months"
              hide-default-footer
              :page.sync="categoryPages[i]"
              :items-per-page="itemsPerPage"
              @page-count="categoryPageCounts[i] = $event"
            >
              <template v-slot:body="{ items }">
                <tbody class="nowrap">
                  <template v-for="(budget, i) in items">
                    <tr :key="`category-month-${i}`">
                      <td :class="(i == 0) ? 'font-weight-bold' : ''">{{getMonthName(budget)}}</td>
                      <td class="right">{{ toVatFormat(budget.sales_budget) }}</td>
                      <td class="right">{{ toVatFormat(budget.sales_result) }}</td>
                      <td class="right">{{ budget.sales_achievement }}</td>
                      <td class="right">{{ budget.sales_yoy }}</td>
                      <td class="right">{{ toVatFormat(budget.row_cost) }}</td>
                      <td class="right">{{ toVatFormat(budget.gross_profit_budget) }}</td>
                      <td class="right">{{ toVatFormat(budget.gross_profit_result) }}</td>
                      <td>
                        <div class="d-flex align-items-center min-width-150">
                          <span class="table-percent-label">{{ budget.gross_profit_achievement }}</span>
                          <div class="progress w-100"><div class="progress-bar progress-bar-striped bg-info" role="progressbar" :style="'width:' + budget.gross_profit_achievement + ''" :aria-valuenow="budget.gross_profit_achievement" aria-valuemin="0" aria-valuemax="100"></div></div>
                        </div>
                      </td>
                      <td class="right">{{ budget.gross_profit_yoy }}</td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-data-table>
            <v-pagination v-model="categoryPages[i]" :length="categoryPageCounts[i]" :total-visible="10"></v-pagination>
          </div>
        </div>
        <hr :class="i != categoryBudgets.length - 1 ? '' : 'hidden'" :key="`category-hr-${i}`">
      </template>
    </div>
    <vue-element-loading :active="isSearching" :is-full-screen="true"/>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import { mapState } from 'vuex'

export default {
  name: 'ArchiveBudgets',
  components: {
    VueElementLoading
  },
  props: {},
  data () {
    return {
      vatRate: 0, // VAT
      taxIncluded: false, // VAT
      isLoading: false,
      budgetYears: [],
      selectedYear: 0,
      wholeBudgets: [],
      contactBudgets: [],
      categoryBudgets: [],
      isSearching: false,
      page: 1,
      pageCount: null,
      itemsPerPage: 10,
      contactPageCounts: [],
      contactPages: [],
      categoryPageCounts: [],
      categoryPages: [],
      monthNames: [
        '', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
      ]
    }
  },
  created: async function () {
    this.isSearching = true
    this.getVatRate() // VAT

    this.selectedYear = this.$storage.get('budgets_search_year') ? parseInt(this.$storage.get('budgets_search_year')) : 0

    await this.loadBudgetYears()

    if (this.selectedYear) {
      await this.listBudgets() // 年度選択時のみ実行
    }
    this.isSearching = false
  },
  computed: {
    ...mapState(['currentLanguage'])
  },
  methods: {
    translations (key) {
      const language = require(`@/locales/${this.currentLanguage}.json`)
      const keys = key.split('.')
      let value = language

      for (const k of keys) {
        value = value[k]
        if (!value) break
      }

      return value || key
    },
    getMonthName (budget) {
      if (budget.month === 0) {
        return this.translations('budget.annual')
      } else {
        if (this.currentLanguage === 'en') {
          const monthNumber = parseInt(budget.month, 10)
          return this.monthNames[monthNumber]
        } else {
          const monthNumber = parseInt(budget.month, 10)
          return monthNumber + this.translations('budget.month')
        }
      }
    },
    async getVatRate () { // VAT
      try {
        let result = await this.$http.post(this.$api_url.vat_rate)
        console.log(JSON.stringify(result.data))
        if (result.data.status === true) {
          this.vatRate = result.data.vat_rate
          this.restoreTax()
        } else {
          this.$toastr.error('消費税率が取得できませんでした。', 'エラー')
          this.taxIncluded = false
          this.$refs['defaultCheckTax'].disabled = true
        }
      } catch (error) {
        console.log('ArchiveBudgets:getVatRate:catch(error): ', error)
        this.$toastr.error('エラーのため失敗しました。ご確認後、再試行してください。', 'エラー')
      }
    },
    restoreTax () { // VAT (rename *****)
      this.taxIncluded = this.$storage.get('ArchiveBudgets.taxIncluded', this.taxIncluded) ? this.$storage.get('ArchiveBudgets.taxIncluded', this.taxIncluded) : false
      console.log(this.taxIncluded)
    },
    toggleTax () { // VAT (rename *****)
      this.$storage.set('ArchiveBudgets.taxIncluded', this.taxIncluded)
      console.log(this.taxIncluded)
    },
    toVatFormat (target, noData = null, taxIncluded = null, vatRate = null) { // VAT OUTPUT
      if (target) {
        taxIncluded = taxIncluded === null ? this.taxIncluded : taxIncluded
        vatRate = vatRate === null ? Number(this.vatRate) : Number(vatRate)
        let tax = Math.round(target * (100 + vatRate) / 100)
        if (!taxIncluded) {
          return `${Number(target).toLocaleString()} (${Number(tax).toLocaleString()})`
        } else {
          return `${Number(tax).toLocaleString()} (${Number(target).toLocaleString()})`
        }
      } else if (noData !== null) {
        return noData
      } else {
        return ''
      }
    },
    noVatFormat (target, noData = null) { // VAT OUTPUT
      if (target) {
        return Number(target).toLocaleString()
      } else if (noData !== null) {
        return noData
      } else {
        return ''
      }
    },
    filterTax (target, taxIncluded = null, vatRate = null) { // VAT INPUT
      target = target || 0
      taxIncluded = taxIncluded === null ? this.taxIncluded : taxIncluded
      vatRate = vatRate === null ? Number(this.vatRate) : Number(vatRate)
      if (!taxIncluded) {
        return target
      } else {
        let noTax = Math.round(target / ((100 + vatRate) / 100))
        return noTax
      }
    },

    async loadBudgetYears () {
      try {
        let params = {}
        let budgetYearsList = await this.$http.post(this.$api_url.budget_years_list, params)
        let $data = this.$invoiceTools.prepareResult(budgetYearsList.data, ['years'], 'ArchiveBudgets:budgetYearsList')
        console.log('ArchiveBudgets:' + this.$api_url.budget_years_list + ':data ', JSON.stringify($data))

        if ($data.success === true) {
          // this.budgetYears = [2019, 2018, 2017, 2016, 2015]
          this.budgetYears = $data.data.years
        } else {
          // this.budgetYears = [2020, 2019] // デフォルトなし
        }
        this.selectYear(this.selectedYear)
      } catch (error) {
        console.log('ArchiveBudgets:loadBudgetYears:catch(error): ', JSON.stringify(error))
        this.$toastr.error('エラーのため失敗しました。ご確認後、再試行してください。', 'エラー')
      }
    },

    async listBudgets () {
      try {
        let params = {
          year: this.selectedYear
        }
        // console.log('ArchiveBudgets:' + this.$api_url.budgets_list + ':params  ', JSON.stringify(params))

        let budgetList = await this.$http.post(this.$api_url.budgets_list, params)

        // console.log('ArchiveBudgets:' + this.$api_url.budgets_list + ':row_response', JSON.stringify(budgetList))

        let $data = this.$invoiceTools.prepareResult(budgetList.data, ['as_whole', 'by_contact', 'by_category'], 'ArchiveBudgets:listBudgets')

        // console.log('ArchiveBudgets:' + this.$api_url.budgets_list + ':data ', JSON.stringify($data))

        if ($data.success === true) {
          this.wholeBudgets = $data.data.as_whole
          this.contactBudgets = $data.data.by_contact
          for (let i = 0; i < this.contactBudgets.length; i++) {
            this.contactPages[i] = 1
          }
          this.categoryBudgets = $data.data.by_category
          for (let i = 0; i < this.categoryBudgets.length; i++) {
            this.categoryPages[i] = 1
          }
        } else {
          this.wholeBudgets = []
          this.contactBudgets = []
          this.categoryBudgets = []

          // テストデータ
          // this.loadBudgetsData()
        }
        // this.selectYear(this.selectedYear)
        // this.calcYearSums() // 既にAPIにて合計済み

        // console.log('ArchiveBudgets:listBudgets:as_whole', JSON.stringify(this.wholeBudgets))
        // console.log('ArchiveBudgets:listBudgets:by_contact', JSON.stringify(this.contactBudgets))
        // console.log('ArchiveBudgets:listBudgets:by_category', JSON.stringify(this.categoryBudgets))
      } catch (error) {
        console.log('ArchiveBudgets:listBudgets:catch(error): ', JSON.stringify(error))
        this.$toastr.error('エラーのため失敗しました。ご確認後、再試行してください。', 'エラー')
      }
    },

    async triggerFilter (field, val) {
      try {
        let params = {
          year: this.selectedYear
        }
        console.log('ArchieveBudgets:' + this.$api_url.budgets_list + 'triggerFilter:params  ', JSON.stringify(params))

        this.$storage.set('budgets_search_year', this.selectedYear)

        this.isSearching = true

        let budgetList = await this.$http.post(this.$api_url.budgets_list, params)

        console.log('ArchiveBudgets:' + this.$api_url.budgets_list + 'triggerFilter:row_response  ', JSON.stringify(budgetList))

        let $data = this.$invoiceTools.prepareResult(budgetList.data, ['as_whole', 'by_contact', 'by_category'], 'ArchiveBudgets:triggerFilter')

        console.log('ArchiveBudgets:' + this.$api_url.budgets_list + 'triggerFilter:data', JSON.stringify(budgetList))

        if ($data.success === true) {
          this.wholeBudgets = $data.data.as_whole
          this.contactBudgets = $data.data.by_contact
          for (let i = 0; i < this.contactBudgets.length; i++) {
            this.contactPages[i] = 1
          }
          this.categoryBudgets = $data.data.by_category
          for (let i = 0; i < this.categoryBudgets.length; i++) {
            this.categoryPages[i] = 1
          }
        } else {
          this.wholeBudgets = []
          this.contactBudgets = []
          this.categoryBudgets = []

          // テストデータ
          // this.loadBudgetsData()
        }

        // this.calcYearSums() // 既にAPIにて合計済み

        console.log('ArchiveBudgets:triggerFilter:as_whole', JSON.stringify(this.wholeBudgets))
        console.log('ArchiveBudgets:triggerFilter:by_contact', JSON.stringify(this.contactBudgets))
        console.log('ArchiveBudgets:triggerFilter:by_category', JSON.stringify(this.categoryBudgets))

        this.isSearching = false
      } catch (error) {
        console.log('ArchiveBudgets:triggerFilter:catch(error): ', JSON.stringify(error))
        this.$toastr.error('エラーのため失敗しました。ご確認後、再試行してください。', 'エラー')
      }
    },

    selectYear (year) {
      if (year === 0) return
      this.budgetYears.forEach(function (v) {
        if (v === year) {
          window.$('#filterByYear').text(v)
        }
      })
    },

    clearFilterByYear () {
      window.$('#filterByYear').text('年度')
    },

    calcYearSums () {
      this.wholeBudgets.unshift(this.calcYearSum(this.wholeBudgets))

      let $this = this
      this.contactBudgets.forEach(function (item) {
        item.months.unshift($this.calcYearSum(item.months))
      })

      this.categoryBudgets.forEach(function (item) {
        item.months.unshift($this.calcYearSum(item.months))
      })
    },

    calcYearSum (data) {
      let tempSalesBudget = 0
      let tempSalesResult = 0
      let tempSalesAchievement = 0
      let tempSalesYoy = 0
      let tempRowCost = 0
      let tempDomesticCost = 0
      let tempGrossBudget = 0
      let tempGrossResult = 0
      let tempGrossAchievement = 0
      let tempGrossYoy = 0

      data.forEach(function (item) {
        tempSalesBudget += item.sales_budget
        tempSalesResult += item.sales_result
        tempSalesAchievement += item.sales_achievement
        tempSalesYoy += item.sales_yoy
        tempRowCost += item.row_cost
        tempDomesticCost += item.domestic_cost
        tempGrossBudget += item.gross_profit_budget
        tempGrossResult += item.gross_profit_result
        tempGrossAchievement += item.gross_profit_achievement
        tempGrossYoy += item.gross_profit_yoy
      })

      return {
        month: '年間',
        sales_budget: tempSalesBudget,
        sales_result: tempSalesResult,
        sales_achievement: Math.floor(tempSalesAchievement / data.length),
        sales_yoy: Math.floor(tempSalesYoy / data.length),
        row_cost: tempRowCost,
        domestic_cost: tempDomesticCost,
        gross_profit_budget: tempGrossBudget,
        gross_profit_result: tempGrossResult,
        gross_profit_achievement: Math.floor(tempGrossAchievement / data.length),
        gross_profit_yoy: Math.floor(tempGrossYoy / data.length)
      }
    },

    openSingleBudget (budget) {
      this.$router.push({ name: 'single-budget', query: { budget_id: budget.budget_id } })
    },

    // テストデータ
    loadBudgetsData () {
      for (let i = 1; i <= 12; i++) {
        this.wholeBudgets = this.loadMonthsData()
      }

      this.contactBudgets.push({
        contacts_id: 1,
        contacts_name: '担当者01',
        budget_id: 1,
        months: this.loadMonthsData()
      },
      {
        contacts_id: 2,
        contacts_name: '担当者02',
        budget_id: 2,
        months: this.loadMonthsData()
      })

      this.categoryBudgets.push({
        category_id: 1,
        category_name: 'カテゴリ01',
        budget_id: 3,
        months: this.loadMonthsData()
      },
      {
        category_id: 2,
        category_name: 'カテゴリ02',
        budget_id: 4,
        months: this.loadMonthsData()
      })
    },

    loadMonthsData () {
      let data = []
      for (let i = 1; i <= 12; i++) {
        data.push(this.loadSingleBudgetData(i))
      }

      return data
    },

    loadSingleBudgetData (month) {
      return {
        month: month,
        sales_budget: Math.floor(Math.random() * 1000000),
        sales_result: Math.floor(Math.random() * 100000),
        sales_achievement: Math.floor(Math.random() * 100),
        sales_yoy: Math.floor(Math.random() * 100),
        row_cost: Math.floor(Math.random() * 10000),
        domestic_cost: Math.floor(Math.random() * 10000),
        gross_profit_budget: Math.floor(Math.random() * 1000000),
        gross_profit_result: Math.floor(Math.random() * 10000),
        gross_profit_achievement: Math.floor(Math.random() * 100),
        gross_profit_yoy: Math.floor(Math.random() * 100)
      }
    }
  }
}
</script>
